import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { splitEvents, type EventItem } from "@/content/site-content";
import { speakerTestimonials, speakingTopics } from "@/content/speaking";
import { SpeakerTestimonialCard } from "@/components/speaker-testimonial-card";
import { PastEvents } from "@/components/past-events";
import ogSocial from "@/assets/og-sylwia-sitkowska.jpg";
import { abs, eventSchema, SITE_URL } from "@/content/seo";
import konferencja from "@/assets/sylwia-scena-alexandrium.jpg.asset.json";
import { cta } from "@/lib/analytics";
import { logoSize } from "@/lib/logo-size";
import { Reveal, useParallax } from "@/components/reveal";
import { PhotoFigure } from "@/components/photo-figure";
import { OrganizerBio } from "@/components/organizer-bio";

const title = "Sylwia Sitkowska, wystąpienia, prelekcje i keynote dla firm";
const description =
  "Sylwia Sitkowska wystąpienia i prelekcje dla firm, konferencji oraz zarządów. Keynote’y, panele eksperckie i spotkania o psychologii przywództwa, odpowiedzialności i decyzjach pod presją.";
const image = abs(ogSocial);

export const Route = createFileRoute("/wystapienia")({
  head: () => {
    const { upcoming, past } = splitEvents();
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: `${SITE_URL}/wystapienia` },
        { property: "og:image", content: image },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: image },
      ],
      links: [{ rel: "canonical", href: `${SITE_URL}/wystapienia` }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "ItemList",
                name: "Nadchodzące wystąpienia Sylwii Sitkowskiej",
                itemListElement: upcoming.map((event, index) => ({
                  "@type": "ListItem",
                  position: index + 1,
                  item: eventSchema(event),
                })),
              },
              {
                "@type": "ItemList",
                name: "Przeszłe wystąpienia Sylwii Sitkowskiej",
                itemListElement: past.map((event, index) => ({
                  "@type": "ListItem",
                  position: index + 1,
                  item: eventSchema(event),
                })),
              },
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Strona główna", item: `${SITE_URL}/` },
                  { "@type": "ListItem", position: 2, name: "Wystąpienia", item: `${SITE_URL}/wystapienia` },
                ],
              },
            ],
          }),
        },
      ],
    };
  },
  component: WystapieniaPage,
});

function UpcomingCard({ event }: { event: EventItem }) {
  return (
    <article
      id={event.id}
      className="scroll-mt-24 flex flex-col gap-5 border border-steel bg-background p-6 sm:flex-row sm:items-start sm:gap-8 md:p-8"
    >
      <div className={`${event.logo ? "flex" : "hidden sm:flex"} h-20 w-32 flex-shrink-0 items-center justify-start sm:w-40`}>
        {event.logo && (
          <img
            src={event.logo}
            alt={`Logo wydarzenia ${event.title}`}
            {...logoSize(event.logo)}
            loading="lazy"
            className="max-h-20 max-w-32 object-contain object-left sm:max-w-40"
          />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="eyebrow text-coral-text">{event.date}</p>
        <h3 className="serif-display mt-4 text-[1.5rem] leading-tight text-ink">{event.title}</h3>
        <p className="mt-3 text-sm text-ink-soft">{event.place}</p>
        <p className="mt-6 text-base leading-relaxed text-ink/80">{event.topic}</p>
        {event.href && (
          <a
            href={event.href}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex min-h-11 items-center text-sm font-medium text-ink underline underline-offset-4 hover:text-coral-text"
          >
            Strona wydarzenia ↗
          </a>
        )}
      </div>
    </article>
  );
}

function WystapieniaPage() {
  const stageRef = useParallax(16);
  const { upcoming, past } = splitEvents();
  const [showAllUpcoming, setShowAllUpcoming] = useState(false);
  const visibleUpcoming = showAllUpcoming ? upcoming : upcoming.slice(0, 3);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="tresc-glowna">
        <section className="relative overflow-hidden border-b border-steel">
          <div aria-hidden="true" className="glow-coral -right-24 -top-24 h-72 w-72" />
          <div className="container-page relative grid items-center gap-10 section-pad lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
            <Reveal>
              <p className="eyebrow">Wystąpienia i współpraca</p>
              <h1 className="serif-display mt-8 max-w-3xl text-[2.4rem] md:text-[3.4rem]">
                Wystąpienia, panele i praca <span className="italic text-coral-text">z organizacjami</span>
              </h1>
              <p className="measure mt-8 border-l-2 border-coral pl-5 text-base leading-relaxed text-ink/80 md:text-lg">
                Występuję na konferencjach i wydarzeniach branżowych, biorę udział w panelach
                dyskusyjnych i prowadzę spotkania zamknięte dla zarządów oraz zespołów
                menedżerskich. Mówię o{" "}
                <Link to="/psychologia-przywodztwa" className="border-b border-coral text-ink transition-colors hover:text-coral-text">psychologii przywództwa</Link>, odpowiedzialności i kosztach,
                które ponoszą ludzie decydujący za innych oraz o tym, jak świadomie nimi zarządzać.
              </p>
              <p className="mt-5 text-sm text-ink-soft">
                Keynote · panel ekspercki · spotkanie zamknięte dla zarządu · warsztat dla kadry menedżerskiej
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3">
                <Link to="/kontakt" {...cta("zapytaj-o-wspolprace", "hero", "prelekcja")} className="btn-base btn-primary inline-flex">Zapytaj o współpracę</Link>
                <a href="#bio" className="inline-flex min-h-11 items-center text-sm font-medium text-ink underline underline-offset-4 hover:text-coral-text">Bio i zdjęcia dla organizatorów</a>
              </div>
              <div className="mt-10 border-t border-steel pt-5">
                <p className="eyebrow">Występowałam m.in. na:</p>
                <p className="mt-3 text-sm leading-relaxed text-ink/80">
                  Polski Kongres Przedsiębiorczości · Midel Camp · Akademia Leona Koźmińskiego · Konferencja Analiza IT · TransportCamp · Diamenty Europy · Akademia Zwierciadła
                </p>
              </div>
            </Reveal>
            <PhotoFigure
              src={konferencja.url}
              alt="Sylwia Sitkowska podczas wystąpienia przed publicznością"
              frameRef={stageRef}
              imgClassName="aspect-[4/3] w-full object-cover object-[48%_40%] md:aspect-[3/2]"
              width={1600}
              height={1067}
              priority
            />
          </div>
        </section>

        <section className="border-b border-steel">
          <div className="container-page section-pad">
            <p className="eyebrow">Zakres</p>
            <h2 className="display-xl mt-6 text-[1.9rem] md:text-[2.6rem]">Przykładowe tematy wystąpień</h2>
            <ul className="mt-8 grid gap-px border border-steel bg-steel lg:grid-cols-4">
              {speakingTopics.map((topic) => (
                <li key={topic.title} className="bg-background p-6">
                  <h3 className="text-base font-semibold leading-snug text-ink">{topic.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink/80">
                    {"link" in topic && topic.description.includes(topic.link.anchor) ? (
                      <>
                        {topic.description.split(topic.link.anchor)[0]}
                        <Link
                          to={topic.link.to}
                          className="underline decoration-steel underline-offset-4 hover:text-ink"
                        >
                          {topic.link.anchor}
                        </Link>
                        {topic.description.split(topic.link.anchor).slice(1).join(topic.link.anchor)}
                      </>
                    ) : (
                      topic.description
                    )}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section id="wydarzenia" className="scroll-mt-24 border-b border-steel">
          <div className="container-page section-pad">
            <h2 className="display-xl text-[1.9rem] md:text-[2.6rem]">Najbliższe wystąpienia</h2>
            <div className="mt-8 flex flex-col gap-5">
              {visibleUpcoming.map((event) => <UpcomingCard key={event.id} event={event} />)}
            </div>
            {upcoming.length > 3 && (
              <button
                type="button"
                onClick={() => setShowAllUpcoming((v) => !v)}
                aria-expanded={showAllUpcoming}
                className="mt-8 inline-flex min-h-11 items-center text-base font-medium text-ink underline underline-offset-4 hover:text-coral-text"
              >
                {showAllUpcoming ? "Zwiń listę" : `Pokaż wszystkie nadchodzące (${upcoming.length})`}
              </button>
            )}
          </div>
        </section>

        <PastEvents past={past} />

        <section className="border-b border-steel">
          <div className="container-page section-pad">
            <p className="eyebrow">Rekomendacje</p>
            <h2 className="display-xl mt-6 text-[1.9rem] md:text-[2.6rem]">
              Z perspektywy <span className="serif-display italic text-coral-text">organizatorów</span>
            </h2>
            <div className="mt-8 grid items-start gap-5 xl:grid-cols-3">
              {speakerTestimonials.map((item) => <SpeakerTestimonialCard key={item.name} item={item} />)}
            </div>
          </div>
        </section>

        <OrganizerBio />

        <section id="kontakt" className="grain scroll-mt-24 bg-ink">
          <div className="container-page py-12 text-mist md:py-16">
            <h2 className="display-xl max-w-2xl text-[1.9rem] md:text-[2.6rem]">
              Porozmawiajmy o wystąpieniu dla Twojej organizacji
            </h2>
            <p className="measure mt-6 text-base leading-relaxed text-mist/80 md:text-lg">
              Napisz, jakie to wydarzenie, kto będzie na sali i jaki temat Was interesuje. Odeślę propozycję tematu i warunki współpracy.
            </p>
            <Link
              to="/kontakt"
              {...cta("zapros-mnie-na-wydarzenie", "domkniecie", "prelekcja")}
              className="btn-base mt-10 inline-flex border border-mist text-mist hover:bg-mist hover:text-ink"
            >
              Zaproś mnie na wydarzenie
            </Link>
            <p className="mt-6 text-sm text-mist/70">
              Więcej o mnie:{" "}
              <Link to="/o-mnie" className="underline underline-offset-4 hover:text-mist">O mnie</Link>
              {" · "}
              <Link to="/media-i-publikacje" className="underline underline-offset-4 hover:text-mist">Media i publikacje</Link>
            </p>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}